/**
 * Iter 86.4 / Phase E6 Sprint 2A — Desktop Agent · Admin Registrations.
 *
 * Issue a one-time registration code for an employee, revoke any active
 * registration, list all registrations.
 *
 * RBAC: workforce:agent_admin (super_admin only by default).
 *
 * Governing principle: presence-monitoring, not surveillance.
 */
import { useCallback, useEffect, useState } from "react";
import {
    ArrowsClockwise, CircleNotch, Copy, Info, Plus, Warning,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

const STATUS_LABEL = {
    pending: { txt: "Pending", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    active: { txt: "Active", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    paused: { txt: "Paused", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    revoked: { txt: "Revoked", cls: "bg-rose-100 text-rose-800 border-rose-200" },
    expired: { txt: "Expired", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function humanAgo(iso) {
    if (!iso) return "—";
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return "—";
    const diffSec = Math.round((Date.now() - ts) / 1000);
    if (diffSec < 60) return "just now";
    const m = Math.round(diffSec / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
}

export default function AdminAgentRegistrations() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [issueOpen, setIssueOpen] = useState(false);
    const [employeeId, setEmployeeId] = useState("");
    const [ttlHours, setTtlHours] = useState(24);
    const [issuing, setIssuing] = useState(false);
    const [issuedCode, setIssuedCode] = useState(null);
    const [revoking, setRevoking] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/workforce/desktop-agent/registrations");
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load registrations"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Defer initial reload via post-commit microtask to satisfy
        // React 19 react-hooks/set-state-in-effect.
        Promise.resolve().then(() => { load(); });
    }, [load]);

    const onIssue = async () => {
        if (!employeeId.trim()) {
            toast.error("Enter an Employee Code");
            return;
        }
        setIssuing(true);
        try {
            const trimmed = employeeId.trim();
            // Admins see EMP-XXXX codes; only send `employee_code` so the
            // backend resolves via the human-readable column. The backend
            // also accepts `employee_id` (UUID) but no admin types UUIDs.
            const { data } = await api.post(
                "/workforce/desktop-agent/registrations:issue",
                { employee_code: trimmed, expires_in_hours: Number(ttlHours) || 24 },
            );
            setIssuedCode(data);
            setIssueOpen(false);
            setEmployeeId("");
            load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to issue registration code"));
        } finally {
            setIssuing(false);
        }
    };

    const onRevoke = async (reg) => {
        if (!confirm(`Revoke registration for ${reg.employee_name || reg.employee_id} (${reg.machine_name || "unclaimed"})?\n\nThe next heartbeat from this device will return 410 and the agent will require re-registration. This action is audited and cannot be silently undone.`)) return;
        setRevoking(reg.registration_id);
        try {
            await api.post(
                "/workforce/desktop-agent/registrations:revoke",
                { registration_id: reg.registration_id, reason: "manual" },
            );
            toast.success(`Revoked ${reg.registration_id}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to revoke"));
        } finally {
            setRevoking(null);
        }
    };

    const copyCode = (txt) => {
        navigator.clipboard?.writeText(txt);
        toast.success("Registration code copied to clipboard");
    };

    return (
        <div data-testid="admin-agent-registrations-page">
            <PageHeader
                eyebrow="Admin · Desktop Agent"
                title="Agent Registrations"
                description="Issue one-time codes to register employee devices with the Desktop Activity Agent. Revoke any registration at any time."
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2" data-testid="reload-registrations-btn">
                            {loading ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
                            Refresh
                        </Button>
                        <Button size="sm" onClick={() => setIssueOpen(true)} className="gap-2" data-testid="issue-code-btn">
                            <Plus size={14} />
                            Issue Code
                        </Button>
                    </div>
                )}
            />

            <div className="px-6 sm:px-8 py-6 space-y-6 max-w-7xl">

                <div
                    className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
                    data-testid="presence-not-surveillance-banner"
                >
                    <Info size={20} weight="fill" className="text-blue-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0 text-xs text-blue-900 leading-relaxed">
                        <span className="font-semibold">Presence-monitoring tool, not a surveillance tool.</span>
                        {" "}Each code is single-use, expires within 24 hours by default, and can be revoked at any time.
                        Codes are stored as hashes — the raw value is shown only once at issue time.
                    </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                            <tr>
                                <th className="text-left px-4 py-2 font-semibold">Employee</th>
                                <th className="text-left px-4 py-2 font-semibold">Machine</th>
                                <th className="text-left px-4 py-2 font-semibold">Status</th>
                                <th className="text-left px-4 py-2 font-semibold">Last Handshake</th>
                                <th className="text-left px-4 py-2 font-semibold">Issued</th>
                                <th className="text-left px-4 py-2 font-semibold">Issued By</th>
                                <th className="text-right px-4 py-2 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {items.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm" data-testid="registrations-empty">
                                        {loading ? "Loading…" : "No registrations yet. Click \"Issue Code\" to create the first one."}
                                    </td>
                                </tr>
                            ) : items.map((r) => {
                                const st = STATUS_LABEL[r.status] || STATUS_LABEL.expired;
                                return (
                                    <tr key={r.registration_id} className="hover:bg-slate-50" data-testid={`registration-row-${r.registration_id}`}>
                                        <td className="px-4 py-2.5">
                                            <div className="font-medium text-slate-800">{r.employee_name || r.employee_id}</div>
                                            <div className="text-[11px] text-slate-500 font-mono">{r.employee_id}</div>
                                        </td>
                                        <td className="px-4 py-2.5 font-mono text-[12px] text-slate-700">
                                            {r.machine_name || <span className="text-slate-400 italic">awaiting claim</span>}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border ${st.cls} text-[11px] font-semibold`}>
                                                {st.txt}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-[12px] text-slate-600">{humanAgo(r.last_handshake_at)}</td>
                                        <td className="px-4 py-2.5 text-[12px] text-slate-600">{humanAgo(r.issued_at)}</td>
                                        <td className="px-4 py-2.5 text-[11px] text-slate-500 font-mono">{r.issued_by_email || r.issued_by || "—"}</td>
                                        <td className="px-4 py-2.5 text-right">
                                            {r.status === "active" || r.status === "pending" || r.status === "paused" ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50"
                                                    disabled={revoking === r.registration_id}
                                                    onClick={() => onRevoke(r)}
                                                    data-testid={`revoke-btn-${r.registration_id}`}
                                                >
                                                    {revoking === r.registration_id ? <CircleNotch size={12} className="animate-spin" /> : <Warning size={12} />}
                                                    Revoke
                                                </Button>
                                            ) : (
                                                <span className="text-[11px] text-slate-400">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Issue Code Dialog */}
            <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
                <DialogContent data-testid="issue-code-dialog">
                    <DialogHeader>
                        <DialogTitle>Issue Registration Code</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Employee Code</label>
                            <Input
                                value={employeeId}
                                onChange={(e) => setEmployeeId(e.target.value)}
                                placeholder="EMP-0866"
                                data-testid="issue-employee-id-input"
                            />
                            <div className="text-[11px] text-slate-500 mt-1">Use the employee code from the Employee Master record (e.g. <span className="font-mono">EMP-0866</span>).</div>
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">Expires in (hours)</label>
                            <Input
                                type="number"
                                min={1}
                                max={168}
                                value={ttlHours}
                                onChange={(e) => setTtlHours(e.target.value)}
                                data-testid="issue-ttl-input"
                            />
                            <div className="text-[11px] text-slate-500 mt-1">1 to 168 hours (7 days max).</div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIssueOpen(false)} disabled={issuing} data-testid="issue-cancel-btn">Cancel</Button>
                        <Button onClick={onIssue} disabled={issuing || !employeeId.trim()} data-testid="issue-confirm-btn">
                            {issuing ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}
                            Issue Code
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Code shown ONCE */}
            <Dialog open={!!issuedCode} onOpenChange={(o) => { if (!o) setIssuedCode(null); }}>
                <DialogContent data-testid="issued-code-dialog">
                    <DialogHeader>
                        <DialogTitle>Registration Code Issued</DialogTitle>
                    </DialogHeader>
                    {issuedCode && (
                        <div className="space-y-4 py-2">
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                <span className="font-semibold">This code is shown only once.</span> Copy it now and share it with the employee. It will not be visible again.
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 text-center">
                                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Registration Code · single-use · {humanAgo(issuedCode.expires_at)} expiry</div>
                                <div className="font-mono text-2xl font-bold text-slate-900 tracking-wider" data-testid="issued-code-value">
                                    {issuedCode.registration_code}
                                </div>
                            </div>
                            <div className="text-[12px] text-slate-600">
                                <div><span className="text-slate-500">Employee:</span> {issuedCode.employee_name || issuedCode.employee_id} <span className="font-mono text-[11px] text-slate-400">({issuedCode.employee_id})</span></div>
                                <div className="mt-1"><span className="text-slate-500">Registration ID:</span> <span className="font-mono">{issuedCode.registration_id}</span></div>
                                <div className="mt-1"><span className="text-slate-500">Expires at:</span> {new Date(issuedCode.expires_at).toLocaleString()}</div>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" className="gap-2 flex-1" onClick={() => copyCode(issuedCode.registration_code)} data-testid="copy-code-btn">
                                    <Copy size={14} /> Copy Code
                                </Button>
                                <Button className="flex-1" onClick={() => setIssuedCode(null)} data-testid="issued-code-done-btn">Done</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
